import type { CopySightResult, CopySightStatus } from '@/types/copysight';
import { cn } from '@/lib/utils';
import { ShieldCheck, ShieldAlert, ScanFace, AlertTriangle } from 'lucide-react';

const STATUS_STYLES: Record<
  Exclude<CopySightStatus, 'skipped'>,
  { label: string; className: string; Icon: typeof ShieldCheck }
> = {
  clean: {
    label: 'No IP found',
    className: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300',
    Icon: ShieldCheck,
  },
  ip_found: {
    label: 'IP detected',
    className: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
    Icon: ShieldAlert,
  },
  face_found: {
    label: 'Face detected',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
    Icon: ScanFace,
  },
  ip_and_face: {
    label: 'IP and face detected',
    className: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
    Icon: ShieldAlert,
  },
  error: {
    label: 'Inspection failed',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
    Icon: AlertTriangle,
  },
};

interface CopySightBadgeProps {
  result?: CopySightResult;
  className?: string;
}

// Renders nothing when the output has not been inspected (or was skipped).
export default function CopySightBadge({ result, className }: CopySightBadgeProps) {
  if (!result || result.status === 'skipped') return null;
  const style = STATUS_STYLES[result.status];
  if (!style) return null;
  const { label, className: colors, Icon } = style;

  return (
    <span
      title={`CopySight: ${label}`}
      className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', colors, className)}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
